import type { Todo } from "../../domain/entities.ts";

export interface MyDayPlan { date: string; taskIds: string[]; planningIndex: number; updatedAt: number }
export type MyDayPlans = Record<string, MyDayPlan>;

export function planFor(plans: MyDayPlans, date: string): MyDayPlan {
  const stored = plans[date];
  return stored ? { date, taskIds: [...new Set(stored.taskIds.filter(id => typeof id === "string"))], planningIndex: Math.max(0, stored.planningIndex || 0), updatedAt: stored.updatedAt || 0 } : { date, taskIds: [], planningIndex: 0, updatedAt: 0 };
}

export function updatePlan(plans: MyDayPlans, plan: MyDayPlan): MyDayPlans {
  return { ...plans, [plan.date]: { ...plan, taskIds: [...new Set(plan.taskIds)], updatedAt: Date.now() } };
}

export function myDayTasks(plan: MyDayPlan, tasks: Todo[]): Todo[] {
  const byId = new Map(tasks.filter(task => !task.deleted && !task.archivedAt).map(task => [task.id, task]));
  return plan.taskIds.flatMap(id => byId.get(id) ? [byId.get(id)!] : []);
}

export function myDaySuggestions(plan: MyDayPlan, tasks: Todo[], userId: string, date: string, carryIds: string[] = []): Todo[] {
  const selected = new Set(plan.taskIds);
  const carried = new Set(carryIds);
  const score = (task: Todo): number =>
    (carried.has(task.id) ? 100 : 0) +
    (task.dueDate && task.dueDate < date ? 50 : 0) +
    (task.priority === "urgent" ? 40 : task.priority === "high" ? 15 : 0) +
    (task.dueDate === date || task.startDate === date ? 30 : 0) +
    ((task.assignedTo || []).includes(userId) ? 10 : 0);
  return tasks.filter(task => !selected.has(task.id) && !task.completed && !task.deleted && !task.archivedAt && score(task) > 0)
    .sort((left, right) => score(right) - score(left) || left.position - right.position || left.title.localeCompare(right.title));
}

export function carryForwardIds(plans: MyDayPlans, date: string, tasks: Todo[]): string[] {
  const previous = Object.keys(plans).filter(key => key < date).sort().at(-1);
  if (!previous) return [];
  const open = new Set(tasks.filter(task => !task.completed && !task.deleted && !task.archivedAt).map(task => task.id));
  return planFor(plans, previous).taskIds.filter(id => open.has(id));
}

export function moveMyDayTask(plan: MyDayPlan, id: string, direction: -1 | 1): MyDayPlan {
  const taskIds = [...plan.taskIds];
  const from = taskIds.indexOf(id); const to = from + direction;
  if (from < 0 || to < 0 || to >= taskIds.length) return plan;
  [taskIds[from], taskIds[to]] = [taskIds[to], taskIds[from]];
  return { ...plan, taskIds };
}
